// Full file: CookiesPolicyDialog.js

import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Typography, Checkbox, FormControlLabel, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const CookiesPolicyDialog = ({ open, handleClose, scrollToAge = false }) => {
    const [showFullPolicy, setShowFullPolicy] = useState(false);
    const [analyticsCookies, setAnalyticsCookies] = useState(true);
    const [marketingCookies, setMarketingCookies] = useState(true);
    const [isHighlighted, setIsHighlighted] = useState(false);
    const ageRef = useRef(null);

    const theme = useTheme();

    // When opened with scrollToAge, force full policy visible
    useEffect(() => {
        if (open && scrollToAge) {
            setShowFullPolicy(true);
        }
    }, [open, scrollToAge]);

    // Once full policy is rendered and ref is available, scroll + highlight
    useEffect(() => {
        if (open && scrollToAge && showFullPolicy && ageRef.current) {
            const timer = setTimeout(() => {
                ageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setIsHighlighted(true);
                setTimeout(() => setIsHighlighted(false), 2600);
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [open, scrollToAge, showFullPolicy]);

    // Reset highlight when dialog closes
    useEffect(() => {
        if (!open) setIsHighlighted(false);
    }, [open]);

    const handleShowFullPolicy = () => {
        setShowFullPolicy(true);
    };

    const handleAcceptPolicy = () => {
        // Save preferences to local storage
        const preferences = {
            essential: true, // Always true for essential cookies
            analytics: analyticsCookies,
            marketing: marketingCookies,
        };
        localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
        localStorage.setItem('termsAccepted', 'true');
        handleClose();
    };

    // Determine button text based on cookie preferences
    const buttonText =
        analyticsCookies && marketingCookies
            ? 'Accept All & Close'
            : 'Accept & Close';

    return (
        <Dialog open={open} onClose={() => {}} maxWidth="md" fullWidth>
            <DialogTitle sx={{ backgroundColor: theme.palette.primary.light, color: theme.palette.primary.main }}>
                Cookies, Privacy Policy & Safety Disclaimer
            </DialogTitle>
            <DialogContent dividers>
                <DialogContentText component="div">
                    <Box>
                        <Typography variant="body1">
                            We use cookies and tracking technologies to enhance your experience on ZOSA. This includes tracking through analytics and other services to analyse performance and improve functionality. By using our site, you agree to this data collection and our safety protocols.
                        </Typography>

                        {!showFullPolicy && (
                            <Button
                                onClick={handleShowFullPolicy}
                                color="primary"
                                sx={{ mt: 1, pl: 0, textTransform: 'none', textDecoration: 'underline' }}
                            >
                                Read Full Cookies, Privacy & Safety Policy
                            </Button>
                        )}

                        {showFullPolicy && (
                            <>
                                <Typography variant="h6" sx={{ mt: 3 }}>1. Use of Cookies and Tracking</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    Cookies are small text files stored on your device to collect information about your activity. ZOSA uses cookies to enhance your experience, track site performance, and analyse user behaviour. We employ analytics services to help us understand how our users interact with the platform and to optimise site performance.
                                </Typography>

                                <Typography variant="h6">2. Analytics and Tracking</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    Our analytics tools help us monitor site usage and behaviour, providing insights into how users engage with the platform. These services collect information such as IP addresses, browser types, and pages visited.
                                </Typography>

                                <Typography variant="h6">3. Marketing Cookies</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    Marketing cookies allow us to provide personalised ads and recommendations. These cookies track your activity to help us deliver relevant marketing content.
                                </Typography>

                                <Typography variant="h6">4. Managing Cookies</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    You can control and manage your cookies through your browser settings. However, please note that disabling cookies may limit certain functionalities of the platform, including personalised content and login preferences.
                                </Typography>

                                <Typography variant="h6">5. Data Security, Encryption & Safety Scanning</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    Your privacy is paramount. Your conversation data is encrypted to protect it from unauthorized access. However, to ensure user safety, <strong>ZOSA employs automated safety algorithms that scan encrypted data specifically for patterns related to severe self-harm or suicide risks.</strong> 
                                </Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    This automated scanning is performed confidentially by non-human systems without manual oversight, ensuring your data remains private and secure while prioritizing your safety.
                                </Typography>

                                <Typography variant="h6">6. User Responsibility</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    Please avoid sharing personal sensitive information such as home addresses, credit card details, or other confidential data within the platform. Exercising caution helps protect your privacy and security.
                                </Typography>

                                <Typography variant="h6">7. Terms and Conditions</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    By using ZOSA, you also agree to comply with our Terms and Conditions, which outline your responsibilities as a user, including providing accurate information and following lawful use of the platform.
                                </Typography>

                                <Box
                                    ref={ageRef}
                                    sx={isHighlighted ? {
                                        borderRadius: '8px',
                                        padding: '8px',
                                        margin: '-8px',
                                        animation: 'ageHighlight 2.6s ease-out forwards',
                                        '@keyframes ageHighlight': {
                                            '0%':   { backgroundColor: 'transparent' },
                                            '15%':  { backgroundColor: 'rgba(105, 143, 253, 0.25)' },
                                            '40%':  { backgroundColor: 'rgba(105, 143, 253, 0.12)' },
                                            '65%':  { backgroundColor: 'rgba(105, 143, 253, 0.25)' },
                                            '100%': { backgroundColor: 'transparent' },
                                        },
                                    } : {}}
                                >
                                    <Typography variant="h6">8. Age Requirement</Typography>
                                    <Typography variant="body2" mt={1} paragraph>
                                        By registering, you confirm that you are 18 years of age or older. ZOSA is an adult mental health platform and access is restricted to individuals aged 18 and over. If you are under 18, you must not use ZOSA. If you believe a minor has accessed this platform, please contact us at hello@zosa.app.
                                    </Typography>
                                </Box>

                                <Typography variant="h6">9. AI Limitations & Accuracy</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    ZOSA is an Artificial Intelligence, not a human expert. While every effort is made to ensure helpful, thoughtful responses, AI can make mistakes — it may occasionally provide inaccurate information, misread context, or offer guidance that does not apply to your specific situation. Nothing ZOSA says should be treated as factual, clinical, or professional advice. Please approach every response with your own critical thinking and judgement. If something does not feel right, trust yourself — and where in doubt, seek guidance from a qualified professional.
                                </Typography>

                                <Typography variant="h6">10. Emergency Protocol</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    ZOSA is <strong>not</strong> equipped to handle active emergencies or crises in real-time. If you or someone else is in immediate danger, or if you are experiencing a medical emergency, you must contact local emergency services immediately (e.g., 911, 000, 999) or visit the nearest emergency room.
                                </Typography>

                                <Typography variant="h6">11. Safety Policy</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    For a detailed breakdown of how we handle user safety, risk detection, and our operational boundaries, please review our full <Link href="/safety" target="_blank" rel="noopener">Safety Policy</Link>.
                                </Typography>

                                <Typography variant="h6">12. Contact Us</Typography>
                                <Typography variant="body2" mt={1} paragraph>
                                    If you have any questions regarding this policy, feel free to reach out to us at hello@zosa.app.
                                </Typography>
                            </>
                        )}

                        <Typography variant="h6" sx={{ mt: 3 }}>Cookie Preferences</Typography>
                        <FormControlLabel
                            control={<Checkbox checked disabled />}
                            label="Essential Cookies (Required)"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={analyticsCookies} onChange={(e) => setAnalyticsCookies(e.target.checked)} />}
                            label="Analytics Cookies"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={marketingCookies} onChange={(e) => setMarketingCookies(e.target.checked)} />}
                            label="Marketing Cookies"
                        />
                        
                        <Typography variant="h6" sx={{ mt: 3 }}>Critical Disclaimer</Typography>
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'justify', fontWeight: 500 }}>
                            ZOSA is <strong>not</strong> a substitute for a licensed therapist, psychologist, or medical professional. It offers guided mental health support based on AI technology to support general well-being. ZOSA does not diagnose, treat, or prevent mental health conditions.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'justify' }}>
                            The app, its developers, and affiliates are not liable for any outcomes, decisions, or actions taken by users based on interactions with the AI. By using this service, you acknowledge that you are responsible for your own well-being. For more details on our operational limits, please visit our <Link href="/important-info" target="_blank" rel="noopener">Important Information</Link> page.
                        </Typography>

                        {/* Additional Links */}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Link href="/safety" target="_blank" rel="noopener" sx={{ fontWeight: 'bold' }}>Safety Policy</Link>
                            <Link href="https://www.google.com/search?q=mental+health+helpline+near+me" target="_blank" rel="noopener" sx={{ fontWeight: 'bold' }}>Find a Helpline</Link>
                            <Link href="/crisis" target="_blank" rel="noopener" sx={{ fontWeight: 'bold' }}>Crisis Support</Link>
                        </Box>
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', padding: theme.spacing(2) }}>
                <Button
                    onClick={handleAcceptPolicy}
                    color="primary"
                    variant="contained"
                    sx={{ width: '100%', maxWidth: '200px', textTransform: 'none' }}
                >
                    {buttonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CookiesPolicyDialog;